/*
 * Copyright 2026 Sony Corporation
 */

import { useEffect, useMemo, useState } from 'react';

import { PolyHavenError, fetchCatalog, searchSlugs, type AssetType, type CatalogAsset } from '../services/polyhaven';

export interface UseLibraryResult {
  assets: CatalogAsset[];
  /** The categories this type actually uses, most common first. */
  categories: string[];
  category: string | undefined;
  setCategory: (category: string | undefined) => void;
  query: string;
  setQuery: (query: string) => void;
  loading: boolean;
  /** Set when the catalogue or the search could not be reached. */
  error: string | undefined;
}

/** How many search hits are worth showing; beyond this the ranking has stopped meaning much. */
const SEARCH_LIMIT = 60;

/** Enough of a pause to mean "stopped typing", which is our side of §5.6's restraint. */
const SEARCH_IDLE_MS = 350;

/** The chips only help while they are a glance; a long tail of one-asset categories does not. */
const MAX_CATEGORIES = 10;

/** Poly Haven's legacy category array also carries curation tags, which are not categories. */
const isCategory = (name: string): boolean => !name.startsWith('collection:');

const message = (cause: unknown): string => {
  if (cause instanceof PolyHavenError) {
    return cause.status === 429 || cause.status === 503 ? 'Poly Haven is busy. Try again in a moment.' : cause.message;
  }
  return 'Poly Haven could not be reached.';
};

/**
 * The Poly Haven catalogue, for one asset type.
 *
 * The whole type arrives in one response and is filtered in the browser, so
 * chips and typing cost nothing upstream. Only `/search` goes back out, and
 * only once the user stops typing — semantic ranking is the one thing we
 * cannot do locally (devplan §5.4).
 *
 * The caller mounts one of these per tab, so switching tabs resets the search
 * and the chips without anything here having to undo them.
 */
export const useLibrary = (type: AssetType): UseLibraryResult => {
  // Undefined until the catalogue answers: loading is derived from that rather
  // than tracked, which keeps every setState in a callback where it belongs.
  const [catalog, setCatalog] = useState<CatalogAsset[]>();
  const [error, setError] = useState<string>();
  const [search, setSearch] = useState<{ query: string; slugs: string[] }>();
  const [category, setCategory] = useState<string>();
  const [query, setQuery] = useState('');

  useEffect(() => {
    const controller = new AbortController();

    fetchCatalog(type, controller.signal).then(
      (assets) => setCatalog(assets),
      (cause: unknown) => {
        if (!controller.signal.aborted) {
          setCatalog([]);
          setError(message(cause));
        }
      }
    );

    return () => controller.abort();
  }, [type]);

  const trimmed = query.trim();

  useEffect(() => {
    if (trimmed.length === 0) {
      return;
    }

    const controller = new AbortController();
    const timer = setTimeout(() => {
      searchSlugs(trimmed, type, SEARCH_LIMIT, controller.signal).then(
        (slugs) => setSearch({ query: trimmed, slugs }),
        (cause: unknown) => {
          if (!controller.signal.aborted) {
            // An empty grid would read as "we have nothing like this", which is
            // not what a 429 means (devplan §5.4).
            setSearch({ query: trimmed, slugs: [] });
            setError(message(cause));
          }
        }
      );
    }, SEARCH_IDLE_MS);

    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [trimmed, type]);

  const categories = useMemo(() => {
    const counts = new Map<string, number>();
    for (const asset of catalog ?? []) {
      for (const name of asset.categories.filter(isCategory)) {
        counts.set(name, (counts.get(name) ?? 0) + 1);
      }
    }
    return [...counts.entries()]
      .sort((a, b) => b[1] - a[1])
      .slice(0, MAX_CATEGORIES)
      .map(([name]) => name);
  }, [catalog]);

  // The last completed search stands until the next one lands, so the grid does
  // not flash back to the whole catalogue between keystrokes.
  const ranking = trimmed.length > 0 ? search?.slugs : undefined;

  const assets = useMemo(() => {
    const all = catalog ?? [];
    const filtered = category ? all.filter((asset) => asset.categories.includes(category)) : all;
    if (!ranking) {
      return filtered;
    }

    // The array order *is* the ranking, so it is what orders the grid rather
    // than the catalogue's own popularity sort.
    const allowed = new Set(filtered.map((asset) => asset.slug));
    const bySlug = new Map(all.map((asset) => [asset.slug, asset]));
    return ranking.flatMap((slug) => {
      const asset = bySlug.get(slug);
      return asset && allowed.has(slug) ? [asset] : [];
    });
  }, [catalog, category, ranking]);

  return { assets, categories, category, setCategory, query, setQuery, loading: catalog === undefined, error };
};
